// Replay is a PURELY CLIENT-SIDE, READ-ONLY re-presentation of a completed
// round. It NEVER touches the network, the wallet, the state machine, or the
// idempotency keys: it only reconstructs the visual inputs from already-stored
// authoritative history and hands them to the same animator the live flow uses.
import type { PublicReplay } from '@aerofruits/game-sdk';
import type { HistoryEntry } from './controller.js';
import type { LightningNumberSlot } from './lightning-types.js';

export interface ReplayPlan {
  roundId: string;
  selectedNumber: number;
  dice: number[];
  matchFlags: boolean[];
  matchCount: number;
  /** Profit multiplier in tenths (presentation). */
  profitMultiplierTenths: number;
  baseProfitMultiplierTenths: number;
  lightningProfitMultiplierTenths: number;
  totalPayoutMultiplierTenths: number;
  betMinor: number;
  winMinor: number;
  currency: string;
  timestampMs: number;
  lightningSlots: LightningNumberSlot[];
  selectedIsLightning: boolean;
  appliedLightningMultiplierTenths: number;
  profitAmountMinor: number;
  stakeReturnMultiplierTenths?: number;
  stakeReturnAmountMinor?: number;
  totalPayoutAmountMinor: number;
}

export function planReplay(entry: HistoryEntry): ReplayPlan | null {
  if (!Array.isArray(entry.dice) || entry.dice.length === 0) return null;
  const dice = entry.dice.map((d) => (Number.isInteger(d) && d >= 1 && d <= 6 ? d : 1));
  const selectedNumber = entry.selectedNumber;
  const matchFlags = dice.map((d) => d === selectedNumber);
  const derived = matchFlags.filter(Boolean).length;
  const matchCount = Number.isInteger(entry.matchCount) ? entry.matchCount : derived;
  const applied = entry.lightningMultiplierTenths ?? 0;
  const baseProfit = entry.baseMultiplierTenths ?? matchCount * 5;
  const lightningProfit = applied > 0 ? matchCount * applied : 0;
  const profitTenths = baseProfit + lightningProfit;
  const lightningSlots: LightningNumberSlot[] =
    applied > 0
      ? [{ number: selectedNumber, tierId: entry.lightningTier ?? 'mid', tenths: applied }]
      : [];
  return {
    roundId: entry.roundId,
    selectedNumber,
    dice,
    matchFlags,
    matchCount,
    profitMultiplierTenths: profitTenths,
    baseProfitMultiplierTenths: baseProfit,
    lightningProfitMultiplierTenths: lightningProfit,
    totalPayoutMultiplierTenths: entry.totalMultiplierTenths ?? entry.multiplier ?? 0,
    betMinor: entry.betMinor,
    winMinor: entry.winMinor,
    currency: entry.currency,
    timestampMs: entry.timestampMs,
    lightningSlots,
    selectedIsLightning: applied > 0,
    appliedLightningMultiplierTenths: applied,
    profitAmountMinor: 0,
    totalPayoutAmountMinor: entry.winMinor,
  };
}

export function planReplayFromPublic(r: PublicReplay): ReplayPlan | null {
  if (!Array.isArray(r.dice) || r.dice.length === 0) return null;
  const dice = r.dice.map((d) => (Number.isInteger(d) && d >= 1 && d <= 6 ? d : 1));
  const selectedNumber = r.selected_number;
  const matchFlags = dice.map((d) => d === selectedNumber);
  const derived = matchFlags.filter(Boolean).length;
  const matchCount = Number.isInteger(r.match_count) ? r.match_count : derived;
  const slots = (r.lightning_slots ?? []).map((s) => ({
    number: s.number,
    tierId: s.tier_id,
    tenths: s.tenths,
  }));
  return {
    roundId: r.replay_id,
    selectedNumber,
    dice,
    matchFlags,
    matchCount,
    profitMultiplierTenths: r.profit_multiplier_tenths ?? 0,
    baseProfitMultiplierTenths: r.base_profit_multiplier_tenths ?? 0,
    lightningProfitMultiplierTenths: r.lightning_profit_multiplier_tenths ?? 0,
    totalPayoutMultiplierTenths: r.total_payout_multiplier_tenths ?? r.total_multiplier_tenths ?? 0,
    betMinor: r.bet_minor,
    winMinor: r.win_minor,
    currency: r.currency,
    timestampMs: r.created_at_unix > 0 ? r.created_at_unix * 1000 : Date.now(),
    lightningSlots: slots,
    selectedIsLightning: r.selected_is_lightning ?? false,
    appliedLightningMultiplierTenths: r.applied_lightning_multiplier_tenths ?? 0,
    profitAmountMinor: r.profit_amount_minor ?? 0,
    stakeReturnMultiplierTenths: r.stake_return_multiplier_tenths ?? 0,
    stakeReturnAmountMinor: r.stake_return_amount_minor ?? 0,
    totalPayoutAmountMinor: r.win_minor,
  };
}
